import { motionToCss, sampleCardMotion } from '@/lib/cardCarousel/animation';
import { isCardOverlayClip } from '@/lib/cardCarousel/model';

const FIT = {
  cover: 'cover',
  contain: 'contain',
  crop: 'cover',
  smart: 'cover',
};

export function CardFrame({
  clip,
  localTime = 0,
  playing = false,
  selected = false,
}) {
  const params = clip?.carouselParams || {};
  const motion = sampleCardMotion({
    localTime,
    clipDuration: Number(clip?.duration) || 2,
    animationIn: params.animationIn || 'popIn',
    animationOut: params.animationOut || 'fadeOut',
    animationDuration: params.animationDuration || 0.4,
    clipOpacity: Number(clip?.opacity) > 0.05 ? Number(clip.opacity) : 1,
    alternatingDirection: Boolean(params.alternatingDirection),
    index: Number(clip?.carouselIndex) || 0,
  });
  const css = playing || localTime > 0 ? motionToCss(motion) : motionToCss({
    opacity: Number(clip?.opacity) > 0.05 ? Number(clip.opacity) : 1,
    scale: 1,
    tx: 0,
    ty: 0,
    rotate: 0,
    blur: 0,
  });
  const radius = `${Math.round((Number(clip?.borderRadius) || params.borderRadius || 0.14) * 28)}px`;
  const title = clip?.cardTitle || clip?.name || '';
  const number = clip?.cardNumber || '';
  const highlight = String(clip?.cardHighlight || '').trim();
  const fit = FIT[params.imageFit] || 'cover';
  const src = clip?.imageUrl || clip?.image_url || '';
  const label = number ? `${number}. ${title}` : title;
  const titleAnim = params.titleAnim || 'fade';
  const titleMotion = (() => {
    const t = Math.min(1, (Number(localTime) || 0) / Math.max(0.15, Number(params.animationDuration) || 0.4));
    if (titleAnim === 'slide') return { opacity: Math.min(1, t * 1.4), transform: `translateY(${(1 - t) * 8}px)` };
    if (titleAnim === 'scale') return { opacity: Math.min(1, t * 1.4), transform: `scale(${0.88 + 0.12 * t})` };
    if (titleAnim === 'typewriter' || titleAnim === 'character') {
      const n = Math.max(1, Math.round(label.length * t));
      return { clipText: label.slice(0, n) };
    }
    if (titleAnim === 'word') {
      const words = label.split(/\s+/);
      const n = Math.max(1, Math.round(words.length * t));
      return { clipText: words.slice(0, n).join(' ') };
    }
    return { opacity: Math.min(1, t * 1.6) };
  })();

  return (
    <div
      className="absolute inset-0 flex flex-col overflow-hidden"
      style={{
        ...css,
        borderRadius: radius,
        boxShadow: params.shadow === false ? 'none' : '0 10px 28px rgba(0,0,0,0.45)',
        border: selected ? '2px solid #facc15' : '1px solid rgba(255,255,255,0.22)',
        background: '#11151c',
        transformOrigin: 'center bottom',
      }}
      data-testid="card-carousel-frame"
    >
      <div className="px-1.5 pt-1.5 pb-1 shrink-0">
        <p
          className="text-[10px] sm:text-[11px] font-black leading-tight tracking-wide text-white uppercase truncate"
          style={{ opacity: titleMotion.opacity, transform: titleMotion.transform, transformOrigin: 'left center' }}
        >
          {titleMotion.clipText || (
          highlight && title.toLowerCase().includes(highlight.toLowerCase()) ? (
            <>
              {number ? `${number}. ` : ''}
              {title.split(new RegExp(`(${highlight})`, 'i')).map((part, idx) => (
                part.toLowerCase() === highlight.toLowerCase() ? (
                  <span key={idx} className="bg-[#e8ff47] text-black px-0.5 rounded-sm">{part}</span>
                ) : <span key={idx}>{part}</span>
              ))}
            </>
          ) : label)}
        </p>
      </div>
      <div className="relative flex-1 min-h-0 mx-1 mb-1 overflow-hidden rounded-[10px] bg-slate-800">
        {src ? (
          <img src={src} alt={title} className="absolute inset-0 w-full h-full pointer-events-none select-none" style={{ objectFit: fit }} draggable={false} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-600 to-slate-900" />
        )}
      </div>
    </div>
  );
}

export function cardClipLocalTime(clip, timelineTime) {
  return Math.max(0, (Number(timelineTime) || 0) - (Number(clip?.startTime) || 0));
}

export { isCardOverlayClip };
